import type { WorkflowEdge, WorkflowNode } from "@/types/aiToolkit";

export function getOutgoingEdges(edges: WorkflowEdge[], nodeId: string): WorkflowEdge[] {
  return edges.filter((e) => e.source === nodeId);
}

export function getIncomingEdges(edges: WorkflowEdge[], nodeId: string): WorkflowEdge[] {
  return edges.filter((e) => e.target === nodeId);
}

export function getPriorNodeId(edges: WorkflowEdge[], nodeId: string): string | undefined {
  const incoming = getIncomingEdges(edges, nodeId);
  return incoming[0]?.source;
}

export function findNode(nodes: WorkflowNode[], nodeId: string): WorkflowNode | undefined {
  return nodes.find((n) => n.id === nodeId);
}

export function findEntryNode(nodes: WorkflowNode[], entryNodeId?: string | null): WorkflowNode | undefined {
  if (entryNodeId) {
    const n = findNode(nodes, entryNodeId);
    if (n) return n;
  }
  return nodes.find((n) => n.type === "trigger") ?? nodes[0];
}

export function resolveNextNodeIds(
  node: WorkflowNode,
  edges: WorkflowEdge[],
  branchKey?: string
): string[] {
  const outgoing = getOutgoingEdges(edges, node.id);
  if (outgoing.length === 0) return [];

  if (node.type === "condition" || node.type === "confidence-gate" || node.type === "quality-gate") {
    const match = outgoing.find((e) => e.sourceHandle === branchKey || e.condition === branchKey || e.label === branchKey);
    if (match) return [match.target];
    const defaultEdge = outgoing.find((e) => e.sourceHandle === "default" || !e.condition);
    return defaultEdge ? [defaultEdge.target] : [outgoing[0].target];
  }

  // 4A.4: loop node branches on "loop" (body, cycles back) vs "done" (continue).
  // Prefer explicitly labeled handles; deterministic fallback = first edge for
  // the body, second (or first) for done.
  if (node.type === "loop") {
    const match = outgoing.find((e) => e.sourceHandle === branchKey || e.condition === branchKey || e.label === branchKey);
    if (match) return [match.target];
    if (branchKey === "done") return [outgoing[1]?.target ?? outgoing[0].target];
    return [outgoing[0].target];
  }

  if (node.type === "parallel") {
    return outgoing.map((e) => e.target);
  }

  // 4A.7: a crew node's "worker" edges point to its worker agents (owned by the
  // crew executor, not traversed). The continuation is the first non-worker edge.
  if (node.type === "crew") {
    const cont = outgoing.find((e) => e.sourceHandle !== "worker");
    return cont ? [cont.target] : [];
  }

  return [outgoing[0].target];
}

export function applyDataMapping(
  mapping: Record<string, string> | undefined,
  state: Record<string, unknown>
): Record<string, unknown> {
  if (!mapping) return {};
  const result: Record<string, unknown> = {};
  for (const [key, path] of Object.entries(mapping)) {
    result[key] = getPathValue(state, path);
  }
  return result;
}

export function getPathValue(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split(".");
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

/**
 * 4A.5 — edge-level data-flow. Resolve every incoming edge's `dataMapping`
 * against the run-state bag ({ variables, nodeOutputs, inputs }) and merge into
 * one input object for the target node. Edges are applied in array order;
 * later edges win on key collision. Returns {} when no incoming edge carries a
 * mapping, so nodes with no wired inputs behave exactly as before (legacy bus).
 */
export function resolveNodeInputs(
  nodeId: string,
  edges: WorkflowEdge[],
  stateBag: Record<string, unknown>,
): Record<string, unknown> {
  const incoming = getIncomingEdges(edges, nodeId);
  const inputs: Record<string, unknown> = {};
  for (const e of incoming) {
    if (!e.dataMapping) continue;
    Object.assign(inputs, applyDataMapping(e.dataMapping, stateBag));
  }
  return inputs;
}

/** Forward-reachable node ids from `startId` (bounded), following edge direction. */
export function forwardReachable(startId: string, edges: WorkflowEdge[], cap = 200): Set<string> {
  const seen = new Set<string>();
  const queue = [startId];
  while (queue.length && seen.size < cap) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const e of edges) if (e.source === id && !seen.has(e.target)) queue.push(e.target);
  }
  seen.delete(startId);
  return seen;
}

/**
 * 4A.6 — locate the topological JOIN where a parallel node's branches converge.
 * The join is the node reachable from EVERY branch (intersection of each
 * branch's forward-reachable set) that sits closest to the branches. Returns
 * null when the branches never reconverge (independent fan-out). Structural
 * only — no execution state — so it's deterministic and unit-testable.
 */
export function findParallelJoin(branchTargets: string[], edges: WorkflowEdge[]): string | null {
  if (branchTargets.length < 2) return null;
  const reach = branchTargets.map((t) => {
    const r = forwardReachable(t, edges);
    r.add(t); // a branch target can itself be the join if another branch feeds into it
    return r;
  });
  let common = [...reach[0]].filter((id) => reach.every((r) => r.has(id)) && !branchTargets.includes(id));
  // Prefer a join that also has incoming edges from ≥2 distinct branches.
  const trueJoins = common.filter((id) => {
    const sources = new Set(getIncomingEdges(edges, id).map((e) => e.source));
    const branchesFeeding = reach.filter((r, i) => [...sources].some((s) => r.has(s) || s === branchTargets[i]));
    return branchesFeeding.length >= 2;
  });
  if (trueJoins.length) common = trueJoins;
  if (!common.length) return null;
  // Closest to the branches = smallest summed BFS distance.
  const dist = (from: string, to: string): number => {
    const q: Array<[string, number]> = [[from, 0]];
    const seen = new Set<string>();
    while (q.length) {
      const [id, d] = q.shift()!;
      if (id === to) return d;
      if (seen.has(id)) continue;
      seen.add(id);
      for (const e of edges) if (e.source === id) q.push([e.target, d + 1]);
    }
    return Infinity;
  };
  return common
    .map((id) => ({ id, score: branchTargets.reduce((s, t) => s + dist(t, id), 0) }))
    .sort((a, b) => a.score - b.score)[0].id;
}

/** The nodes that feed directly into `joinId` and are reachable from each branch. */
export function branchTerminalsFeeding(joinId: string, branchTargets: string[], edges: WorkflowEdge[]): string[] {
  const sources = getIncomingEdges(edges, joinId).map((e) => e.source);
  const terminals: string[] = [];
  for (const t of branchTargets) {
    const reach = forwardReachable(t, edges);
    reach.add(t);
    const feeder = sources.find((s) => reach.has(s));
    if (feeder && !terminals.includes(feeder)) terminals.push(feeder);
  }
  return terminals;
}

export function buildBusContextText(
  contextBlocks: Record<string, { title: string; content: string }>,
  nodeOutputs: Record<string, { raw: string }>
): string {
  const parts: string[] = [];
  for (const block of Object.values(contextBlocks)) {
    parts.push(`### ${block.title}\n${block.content}`);
  }
  for (const [nodeId, out] of Object.entries(nodeOutputs)) {
    if (out.raw?.trim()) parts.push(`### Prior step (${nodeId})\n${out.raw}`);
  }
  return parts.join("\n\n");
}
